import { BtmReturnReportedProduct } from "./btm-return-reported-product";
import { IBaseBtmReturnRequestDeliveryOrderProductDto } from "./btm-return-request";
import { IUploadedFileDto } from "../../../../common/model/file-exchange-dto";

export interface IBtmReturnInspectionProduct {
    declarationProductId?: number;
    productId?: number;
    barcode?: string;
    productName: string;
    status: string | undefined;
    comment?: string;
    files?: IUploadedFileDto[];
}

export enum BtmReturnInspectionStatus {
    None,
    Accepted,
    ConditionallyAccepted,
    Refused,
}

export class BtmReturnInspectionProduct {
    private readonly _declarationProductId?: number;
    private readonly _productId?: number;
    private readonly _barcode?: string;
    private readonly _productName: string;
    private readonly _status: BtmReturnInspectionStatus | undefined;
    private readonly _comment?: string;
    private readonly _files?: IUploadedFileDto[];

    private constructor(declarationProductId: number | undefined, productId: number | undefined, barcode: string | undefined,
                        name: string, inspectionStatus: BtmReturnInspectionStatus, comment?: string, files?: IUploadedFileDto[]) {
        this._declarationProductId = declarationProductId;
        this._productId = productId;
        this._barcode = barcode;
        this._productName = name;
        this._status = inspectionStatus;
        this._comment = comment;
        this._files = files;
    }

    get productName() {
        return this._productName;
    }

    get declarationProductId() {
        return this._declarationProductId;
    }

    get productId() {
        return this._productId;
    }

    get barcode() {
        return this._barcode;
    }

    get status() {
        return this._status;
    }

    get comment() {
        return this._comment;
    }

    get files() {
        return this._files;
    }

    private fromDto(dto: IBtmReturnInspectionProduct) {
        return new BtmReturnInspectionProduct(dto.declarationProductId, dto.productId, dto.barcode, dto.productName,
            BtmReturnInspectionProduct.buildInspectionStatus(dto.status), dto.comment, dto.files);
    }

    public toDto() {
        return {
            declarationProductId: this.declarationProductId,
            productId: this.productId,
            barcode: this.barcode,
            productName: this.productName,
            status: this.status,
            comment: this.comment,
            files: this.files,
        } as IBtmReturnInspectionProduct;
    }

    private static buildInspectionStatus(status: string | undefined) {
        switch (status) {
            case "Accepted":
                return BtmReturnInspectionStatus.Accepted;
            case "ConditionallyAccepted":
                return BtmReturnInspectionStatus.ConditionallyAccepted;
            case "Refused":
                return BtmReturnInspectionStatus.Refused;
            default:
                return BtmReturnInspectionStatus.None;
        }
    }

    private inspectionStatusAsString(status: BtmReturnInspectionStatus) {
        switch (status) {
            case BtmReturnInspectionStatus.Accepted:
                return "Accepted";
            case BtmReturnInspectionStatus.ConditionallyAccepted:
                return "ConditionallyAccepted";
            case BtmReturnInspectionStatus.Refused:
                return "Refused";
            default:
                return "None";
        }
    }

    public inspect(status: BtmReturnInspectionStatus | undefined, comment?: string, files?: IUploadedFileDto[]) {
        return this.fromDto({
            ...this.toDto(),
            status: this.inspectionStatusAsString(status ?? BtmReturnInspectionStatus.None),
            comment,
            files,
        });
    }

    public static fromReportedProduct(reportedProduct: BtmReturnReportedProduct) {
        return Array(reportedProduct.quantity).fill(0).map(() =>
            new BtmReturnInspectionProduct(reportedProduct.id, undefined, undefined, reportedProduct.name, BtmReturnInspectionStatus.None));
    }

    public static fromDeliveryOrderDetail(deliveryOrderDetail: IBaseBtmReturnRequestDeliveryOrderProductDto) {
        return Array(deliveryOrderDetail.quantity).fill(0).map(() =>
            new BtmReturnInspectionProduct(undefined, deliveryOrderDetail.productId, undefined, deliveryOrderDetail.productName, BtmReturnInspectionStatus.None));
    }

    public static fromForm(barcode: string, productName: string) {
        return new BtmReturnInspectionProduct(undefined, undefined, barcode, productName, BtmReturnInspectionStatus.None);
    }

    public static fromDataObject(dto: IBtmReturnInspectionProduct) {
        return new BtmReturnInspectionProduct(dto.declarationProductId, dto.productId, dto.barcode, dto.productName,
            BtmReturnInspectionProduct.buildInspectionStatus(dto.status), dto.comment, dto.files);
    }
}
